import { AutoComplete } from "antd";
import React, { useContext, useEffect, useState } from "react";

import { GeosuggestContext } from "./context";
import { GeosuggestProps } from "./types";

import { googleLoader } from "~/utils";

export const Geosuggest: React.FC<GeosuggestProps> = ({
  value,
  onSelect,
  onSearch,
  defaultValue = "",
  disabled,
  placeholder,
  autoCompleteProps,
  autoCompletePlaceTypes,
}) => {
  const ctx = useContext(GeosuggestContext);
  const [isGmapsLoaded, setIsGmapsLoaded] = useState<boolean>(false);
  const [autocompleteService, setAutocompleteService] =
    useState<google.maps.places.AutocompleteService>();
  const [placesService, setPlacesService] =
    useState<google.maps.places.PlacesService>();
  const [options, setOptions] =
    useState<Array<google.maps.places.AutocompletePrediction> | null>(null);
  const [query, setQuery] = useState<string>(defaultValue || ctx.value);

  // load Google libraries on mount
  useEffect(() => {
    googleLoader.importLibrary("places").then(() => {
      setIsGmapsLoaded(true);
    });
  }, []);

  useEffect(() => {
    setQuery(ctx.value);
  }, [ctx.value]);

  // Sync to controlled or default prop changes
  useEffect(() => {
    if (value !== undefined) {
      setQuery(value);
    } else if (defaultValue !== undefined) {
      setQuery(defaultValue);
    }
  }, [value, defaultValue]);

  useEffect(() => {
    if (isGmapsLoaded) {
      setAutocompleteService(new google.maps.places.AutocompleteService());

      const div = document.createElement("div");
      document.body.append(div);

      setPlacesService(new google.maps.places.PlacesService(div));

      return () => div.remove();
    }
  }, [isGmapsLoaded]);

  useEffect(() => {
    if (autocompleteService && placesService) {
      autocompleteService.getPlacePredictions(
        {
          input: query,
          types: autoCompletePlaceTypes ?? ["address"],
          componentRestrictions: {
            // only countries we can drive to
            country: ["us", "ca", "mx"],
          },
        },
        (opts) => {
          setOptions(opts);

          if (opts && query === defaultValue) {
            const [placeObj] = opts;

            if (placeObj) {
              placesService.getDetails(
                { placeId: placeObj.place_id },
                (a, b) => {
                  setQuery(a?.formatted_address || "");
                  onSelect(a, b);
                }
              );
            }
          }
        }
      );
    }
  }, [autocompleteService, placesService, query]);

  useEffect(() => {
    if (autocompleteService) {
      autocompleteService.getPlacePredictions(
        { input: query, types: ["address"] },
        (opts) => {
          setOptions(opts);

          const placeObj = options?.at(0);

          if (placesService && placeObj?.place_id) {
            placesService.getDetails({ placeId: placeObj.place_id }, onSelect);
          }
        }
      );
    }
  }, [autocompleteService, defaultValue]);

  return (
    <AutoComplete
      notFoundContent={
        query && (
          <span data-testid="geosuggest-no-results">{"No results found"}</span>
        )
      }
      {...autoCompleteProps}
      disabled={disabled}
      placeholder={placeholder}
      data-testid="geosuggest"
      defaultValue={defaultValue}
      options={
        options && query
          ? options.map(({ description }) => ({ value: description }))
          : []
      }
      onSearch={(q) => {
        ctx.setValue(q);
        setQuery(q);
        if (onSearch) {
          onSearch(q);
        }
      }}
      onSelect={(v) => {
        const placeObj = options?.find((opt) => opt.description === v);

        if (placeObj) {
          setQuery(placeObj.description);
        }

        if (placesService && placeObj?.place_id) {
          placesService.getDetails({ placeId: placeObj.place_id }, onSelect);
        }
      }}
      value={query}
    />
  );
};
